<template>
  <div class="moodle-download" :aria-label="pluginLabel + ' download'" :aria-busy="String(loading)">
    <div class="download-heading"><v-icon color="#2563eb">mdi-puzzle-outline</v-icon><h4>Download {{ pluginLabel }}</h4></div>
    <p v-if="loading" role="status">Checking the latest release…</p>
    <div v-else-if="error" role="status" class="download-error">
      <p>The plugin download is temporarily unavailable.</p>
      <button type="button" @click="loadRelease">Try again</button>
    </div>
    <template v-else-if="release">
      <p class="download-version"><strong>Version {{ release.release }}</strong><span>{{ release.maturity }}</span><span>Released {{ release.releasedAt }}</span></p>
      <p>Minimum Moodle {{ release.minimumMoodle }} · Tested on {{ release.testedMoodle.join(', ') }}</p>
      <a class="download-button" :href="downloadUrl"><v-icon small>mdi-download</v-icon>Download {{ pluginLabel }} <span>ZIP · {{ packageSize }}</span></a>
      <details>
        <summary>Release notes and checksum</summary>
        <ul><li v-for="change in release.changes" :key="change">{{ change }}</li></ul>
        <span class="checksum-label">SHA-256</span><code class="checksum">{{ release.sha256 }}</code>
      </details>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import {settings} from '@/Settings';

// The guide is public: use a client without login headers or authentication redirects.
const publicDownloads = axios.create({baseURL: settings.API_SERVER, timeout: 15000});

export default {
  name: 'MoodlePluginDownload',
  props: {
    component: {type: String, default: 'local_hrdintegration'},
    downloadRoot: {type: String, default: 'downloads/moodle'},
    pluginLabel: {type: String, default: 'Moodle HRD plugin'},
  },
  data: () => ({release: null, loading: false, error: false}),
  computed: {
    downloadUrl() {
      return new URL(this.downloadRoot + '/' + encodeURIComponent(this.release.filename), settings.API_SERVER).href;
    },
    packageSize() { return Math.ceil(this.release.sizeBytes / 1024) + ' KB'; },
  },
  created() { this.loadRelease(); },
  methods: {
    async loadRelease() {
      this.loading = true;
      this.error = false;
      try {
        const {data} = await publicDownloads.get(this.downloadRoot + '/manifest.json');
        if (data.component !== this.component ||
            !new RegExp('^' + this.component + '-\\d+\\.\\d+\\.\\d+-\\d{10}\\.zip$').test(data.filename) ||
            !/^[a-f0-9]{64}$/.test(data.sha256) || !Array.isArray(data.testedMoodle) || !Array.isArray(data.changes) ||
            !Number.isFinite(data.sizeBytes) || !data.release || !data.releasedAt || !data.minimumMoodle) {
          throw new Error('Invalid plugin release');
        }
        this.release = data;
      } catch (error) {
        this.release = null;
        this.error = true;
      } finally { this.loading = false; }
    },
  },
};
</script>

<style scoped>
.moodle-download{padding:20px;border:1px solid #bfdbfe;border-radius:12px;background:#f8fbff;margin:18px 0 24px;min-width:0}
.download-heading{display:flex;align-items:center;gap:10px;margin-bottom:12px}.download-heading h4{font-size:15px;font-weight:600;margin:0;color:#0f172a}
.moodle-download p{font-size:12px;line-height:1.8;color:#475569;margin:0 0 10px}.download-version{display:flex;align-items:center;flex-wrap:wrap;gap:8px}.download-version>span:first-of-type{border-radius:5px;background:#e2e8f0;padding:0 7px;text-transform:capitalize}
.download-button{display:inline-flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:8px;padding:11px 15px;border-radius:8px;background:#1d4ed8;color:#fff!important;text-decoration:none;font-size:12px;font-weight:600}.download-button:hover{background:#1e40af}.download-button .v-icon{color:inherit}.download-button>span{font-size:10px;font-weight:400;opacity:.85}
details{margin-top:16px;font-size:12px;color:#475569}summary{cursor:pointer;color:#1d4ed8;font-weight:600}ul{padding-left:20px;margin:12px 0;line-height:1.8}.checksum-label{display:block;font-size:10px;font-weight:600;margin-bottom:5px}.checksum{display:block;background:#eff6ff;color:#1e40af;padding:8px;border-radius:5px;font-size:10px;overflow-wrap:anywhere;box-shadow:none}
.download-error button{border:1px solid #cbd5e1;border-radius:6px;padding:5px 10px;color:#1d4ed8;background:#fff;font-size:12px}.download-button:focus-visible,summary:focus-visible,.download-error button:focus-visible{outline:3px solid #93c5fd;outline-offset:3px}
@media(max-width:600px){.moodle-download{padding:15px}.download-button{width:100%}}
</style>
